import {
  Component,
  computed,
  input,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ActionBarRight,
  AwActionBarComponent,
  AwBadgeComponent,
  AwBreadCrumbComponent,
  AwDialogComponent,
  AwDividerComponent,
  AwSpinnerComponent,
  AwTableComponent,
  AwToastComponent,
  BreadCrumb,
  DialogFormControls,
  DialogOptions,
  SingleSelectOption,
  TableCellInput,
  TableCellTypes,
  TableRow,
  ToastVariants,
  AwButtonDirective,
} from '@assetworks-llc/aw-component-lib';
import { AwTextFormComponent } from '../../../../../shared/components/aw-text-form/aw-text-form.component';
import { AwDropdownFormComponent } from '../../../../../shared/components/aw-dropdown-form/aw-dropdown-form.component';
import { FormsModule } from '@angular/forms';
import { Customer } from '../../../../../api/models';
import { CustomerService } from '../../../../../core/services/customer.service';
import { CommonModule } from '@angular/common';
import {
  AuthTypeValues,
  UpdateOrganizationParams,
} from '../../../../../types/types';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { ThemeService } from '../../../../../core/services/theme.service';
import { ClientSecretService } from '../../../../../core/services/client-secret.service';
import {
  buildClientSecretName,
  computeDaysToExpire,
  getSecretExpiryStatus,
} from '../../../../../shared/utils/secrets.utils';
import { CreateClientSecret$Params } from '../../../../../api/fn/client-secret-management/create-client-secret';
import { DeleteClientSecret$Params } from '../../../../../api/fn/client-secret-management/delete-client-secret';
import { UserService } from '../../../../../core/services/user.service';

import { UnsavedChangesForm } from '../../../../../shared/unsaved-changes/unsaved-changes-form';
import { UnsavedChangesDialogComponent } from '../../../../../shared/unsaved-changes/unsaved-changes-dialog.component';
import { isValidOrganizationName, ORGANIZATION_NAME_MAX_LENGTH, ORGANIZATION_NAME_PATTERN, ORGANIZATION_NAME_PATTERN_MESSAGE } from '../../../../../shared/utils/organization-name';

@Component({
    selector: 'app-customer-details',
    imports: [
        UnsavedChangesDialogComponent,
        AwDividerComponent,
        AwDialogComponent,
        AwTableComponent,
        AwButtonDirective,
        AwTextFormComponent,
        AwDropdownFormComponent,
        AwBreadCrumbComponent,
        AwActionBarComponent,
        AwSpinnerComponent,
        AwToastComponent,
        FormsModule,
        CommonModule,
    ],
    templateUrl: './edit-customer-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './edit-customer-details.component.scss',
        '../../../../../../styles/management.scss',
    ]
})
export class EditCustomerDetailsComponent extends UnsavedChangesForm implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly customerService = inject(CustomerService);
  private readonly themeService = inject(ThemeService);
  private readonly clientSecretService = inject(ClientSecretService);
  private readonly userService = inject(UserService);

  readonly nameMaxLength = ORGANIZATION_NAME_MAX_LENGTH;
  readonly namePattern = ORGANIZATION_NAME_PATTERN;
  readonly namePatternMessage = ORGANIZATION_NAME_PATTERN_MESSAGE;

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  isLoading: boolean = false;
  container_theme = computed(() => this.themeService.getTheme());

  userPermissions: Record<string, boolean> = {};

  openAddCustomerSecretDialog: boolean = false;
  openDeleteCustomerSecretDialog: boolean = false;
  openClientSecretDialog: boolean = false;
  secretToDelete: TableRow | null = null;
  generatedClientSecret: string = '';

  statusOptions = signal<SingleSelectOption[]>([
    {
      label: 'Active',
    },
    {
      label: 'Disabled',
    },
  ]);

  customerInfo!: Customer | undefined;

  selectedDate: Date | null = null;
  customerNameInput: string = '';
  customerIdInput: string = '';
  customerStatusInput: string = '';
  customerStatusValue: boolean | null = null;
  customerAuthTypeValue: AuthTypeValues | undefined;

  actionsRight = input<ActionBarRight[]>([
    {
      buttonCallback: {
        label: 'Cancel',
        buttonType: 'primary',
        action: () =>
          this.router.navigate([
            `/customer-management/customer-details/${this.customerInfo?.organizationId}`,
          ]),
      },
    },
    {
      buttonCallback: {
        label: 'Save',
        buttonType: 'outlined',
        action: () => {
          this.onEditCustomer();
        },
      },
    },
  ]);

  breadcrumbs = signal<BreadCrumb[]>([]);

  secretsColumnsDefinition: TableCellInput[] = [
    {
      type: TableCellTypes.Title,
      key: 'client_secret_name',
      label: 'Expiration Date',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Title,
      key: 'days_remaining',
      label: 'Days until Expiration',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Status',
      combineFields: ['badgeNested.color', 'badgeNested.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.IconButton,
      key: 'random',
      label: 'Remove',
      buttonType: 'primary',
      isExpanded: false,
      icon: 'delete',
      action: (data: TableRow) => {
        this.openDeleteCustomerSecretDialog = true;
        this.secretToDelete = data;
      },
    },
  ];

  secretsTableData = signal<any[]>([]);
  secretsTableDataCopy: any[] = [];

  formFieldsData = input<DialogFormControls>({
    controls: [
      {
        formControlName: 'client_secret_name',
        label: 'Expiration Date',
        value: buildClientSecretName(),
        type: 'text',
        readOnly: true,
      },
    ],
  });

  deleteSecretDialogOptions = input<DialogOptions>({
    variant: 'alert',
    title: 'Delete Customer Secret',
    description:
      'By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  });

  clientSecretDialogOptions = input<DialogOptions>({
    variant: 'alert',
    title: 'Client Secret Created',
    description: 'Keep track of this information. It will not be provided again after this.',
    primaryButtonLabel: 'Close',
  });

  clientSecretFormFields: DialogFormControls = {
    controls: [],
  };

  addSecretDialogOptions = input<DialogOptions>({
    variant: 'standard',
    title: 'Add Customer Secret',
    description: '',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Add',
  });

  dialogFormCloseHandler(key: string) {
    if (key === 'add') {
      this.openAddCustomerSecretDialog = false;
    } else if (key === 'delete') {
      this.openDeleteCustomerSecretDialog = false;
    } else if (key === 'client-secret') {
      this.openClientSecretDialog = false;
      this.generatedClientSecret = '';
    }
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.userPermissions = this.userService.getUserPermissions();

    this.customerIdInput = this.route.snapshot.paramMap.get('customerId')!;

    forkJoin({
      customer: this.customerService
        .getCustomerById({ organizationId: this.customerIdInput })
        .pipe(
          catchError((error) => {
            return of(null);
          })
        ),
      customerSecrets: this.userPermissions['isAdministrator']
        ? this.clientSecretService
            .getClientSecrets({
              organizationId: this.customerIdInput,
            })
            .pipe(
              catchError((error) => {
                return of(null);
              })
            )
        : of(null),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(({ customer, customerSecrets }) => {
        if (!customer) {
          this.buildToastMessage('Customer not found', 'alert');
          return;
        }

        this.customerInfo = customer;

        this.customerAuthTypeValue = customer.authenticationType;

        this.customerNameInput = customer.organizationName || '';
        this.customerStatusValue = customer.enabled || null;
        this.customerStatusInput = customer.enabled ? 'Active' : 'Disabled';
        this.markCleanAfterRender();

        if (customerSecrets) {
          const customerSecretsData: any[] = Object.entries(
            customerSecrets
          ).map(([key, value]) => ({
            client_secret_name: key,
            days_remaining: `${computeDaysToExpire(value)} days`,
            badgeNested: getSecretExpiryStatus(value),
          }));
          this.secretsTableData.set(customerSecretsData);
          this.secretsTableDataCopy = customerSecretsData;
        } else if (this.userPermissions['isAdministrator']) {
          this.buildToastMessage(
            'No client secrets found for customer',
            'alert'
          );
        }
      });

    this.breadcrumbs.set([
      {
        label: 'Customer Management',
        route: '/customer-management/overview',
        newTab: false,
      },
      {
        label: 'Customer Details',
        route: `/customer-management/customer-details/${this.customerIdInput}`,
        newTab: false,
      },
      {
        label: 'Edit Customer Details',
        route: `/customer-management/customer-details/${this.customerIdInput}/edit`,
        newTab: false,
      },
    ]);
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  validateCustomerFields(): boolean {
    return (
      isValidOrganizationName(this.customerNameInput) &&
      this.customerStatusValue !== null &&
      this.customerStatusInput !== '' &&
      this.customerIdInput !== ''
    );
  }

  onEditCustomer(): void {
    if (!this.validateCustomerFields()) {
      this.buildToastMessage('Fill in all required customer fields', 'error');
      return;
    }

    const customerParams: UpdateOrganizationParams = {
      organizationAuthType: this.customerAuthTypeValue,
      organizationName: this.customerNameInput,
      organizationStatus: this.customerStatusValue ?? false,
      organizationPartnerIds: this.customerInfo?.integrationPartnerIds,
      organizationId: this.customerIdInput,
    };

    this.isLoading = true;
    this.customerService.updateCustomer(customerParams)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((customerDetailsUpdated) => {
        if (customerDetailsUpdated) {
          this.buildToastMessage('Successfully updated customer', 'success');
          this.markClean();
        } else {
          this.buildToastMessage('Failed to update customer', 'error');
        }
      });
  }

  addCustomerSecretFormSubmit(formValues: any) {
    if (!formValues.valid) {
      this.buildToastMessage('Please fill in all required fields', 'error');
      return;
    }

    this.isLoading = true;
    const newClientSecretName = formValues.formData.client_secret_name;
    const params: CreateClientSecret$Params = {
      organizationId: this.customerIdInput,
      body: {
        clientSecretName: newClientSecretName,
      },
    };

    this.clientSecretService
      .createClientSecret(params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((clientSecretCreated) => {
        if (!clientSecretCreated || !clientSecretCreated.clientSecret) {
          this.buildToastMessage('Failed to create client secret', 'error');
          return;
        }

        this.generatedClientSecret = clientSecretCreated?.clientSecret ?? '';
        this.clientSecretFormFields = {
          controls: [
            {
              formControlName: 'client_secret',
              label: 'Client Secret',
              value: this.generatedClientSecret,
              type: 'text',
              controlRequired: false,
              readOnly: true,
              fieldActions: [
                {
                  iconButtonCallback: {
                    action: () => this.copyToClipboard(this.generatedClientSecret),
                    icon: "copy",
                    label: "Click to copy the client secret to the clipboard",
                  }
                }
              ],
            },
          ],
        };
        this.openClientSecretDialog = true;
        this.openAddCustomerSecretDialog = false;

        this.secretsTableData.update((secrets) => [
          ...secrets,
          {
            client_secret_name: newClientSecretName,
            days_remaining: `${computeDaysToExpire(newClientSecretName)} days`,
            badgeNested: {
              color: 'success-green-700',
              label: 'Active',
            },
          },
        ]);
      });
  }

  deleteCustomerSecretFormSubmit(formValues: any) {
    this.isLoading = true;
    const params: DeleteClientSecret$Params = {
      organizationId: this.customerIdInput,
      clientSecretName: this.secretToDelete?.['client_secret_name'] || '',
    };
    this.clientSecretService
      .deleteClientSecret(params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((clientSecretDeleted) => {
        if (!clientSecretDeleted) {
          this.buildToastMessage('Failed to delete client secret', 'error');
          return;
        }
        this.buildToastMessage('Successfully deleted client secret', 'success');
        this.openDeleteCustomerSecretDialog = false;

        const filtered = this.secretsTableDataCopy.filter(
          (secret) =>
            secret.client_secret_name !==
            this.secretToDelete?.['client_secret_name']
        );
        this.secretsTableData.set(filtered);
        this.secretsTableDataCopy = [...filtered];
      });
  }

  onDetailsTextEntrySelected(text: string | null, key: string) {
    if (key === 'customer-name') {
      this.customerNameInput = text || '';
    }
  }

  onDateSelected(date: Date | null) {
    this.selectedDate = date;
  }
  onCustomerStatusSelected(status: SingleSelectOption | null) {
    this.customerStatusInput = status?.label || '';
    if (status?.label === 'Active') {
      this.customerStatusValue = true;
    } else if (status?.label === 'Disabled') {
      this.customerStatusValue = false;
    }
  }

  customerTextForms = computed(() => [
    {
      title: 'Customer Name',
      isName: true,
      required: true,
      readOnly: false,
      value: this.customerInfo?.organizationName ?? '',
      textEntryActionHandler: (text: string | null) =>
        this.onDetailsTextEntrySelected(text, 'customer-name'),
    },
    {
      title: 'Customer Id',
      readOnly: true,
      required: true,
      value: this.customerInfo?.organizationId ?? '',
      textEntryActionHandler: (text: string | null) => {},
    },
  ]);

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.buildToastMessage('Copied to clipboard', 'success');
    }).catch(() => {
      this.buildToastMessage('Failed to copy to clipboard', 'error');
    });
  }

  protected formState(): unknown {
    return { name: this.customerNameInput, status: this.customerStatusInput };
  }
}
